import React from 'react'
import { resolve } from 'url'

import pageMetaData from '../../data/pageMetaData'
import config from '../config'

const defaultImage = {
  url: 'https://www.goodcover.com/images/Goodcover-pattern.wordmark.png',
  width: '2500',
  height: '1619',
}

const defaultDescription = config.siteDescriptionMeta

/*
 * Meta Data
 * - title should be no more than 60 characters
 * - description should be no more than 160 ideally
 *
 * @TODO try and tidy this up somehow
 */
export const getPageHead = ({
  noIndex: noIndexProp = false,
  isBlog = false,
  isBlogPost = false,
  isAuthorPage = false,
  isTagPage = false,
} = {}) => {
  const HeadComponent = ({ location, pageContext }) => {
    const {
      title: pageMetaTitle,
      description: pageMetaDescription,
      image: pageMetaImage,
    } = pageMetaData[location.pathname] || {}

    const { author, tag } = pageContext
    // This is to make sure all the /{state} /i/{invite} etc. pages don't get indexed.
    const noIndex = noIndexProp && location.pathname !== '/'

    const title =
      pageMetaTitle ||
      (tag
        ? `Blog posts tagged with '${tag}'`
        : author
          ? `Blog posts by '${author.name}'`
          : null)

    let pageTitle = `${title ? `${title} | ` : ''}${config.siteTitleMeta}`

    let description = pageMetaDescription || defaultDescription

    let canonicalUrl = location && resolve(config.siteUrl, location.pathname)

    let image = pageMetaImage || defaultImage

    const showLDJson = isBlog || isBlogPost || isAuthorPage || isTagPage

    let postAuthor, post, settings, publisherLogo, primaryTag, tags

    let ogTitle = title
    let ogDescription = description

    let twitterTitle = pageTitle
    let twitterDescription = description

    let shareImage = image

    if (isBlog || isBlogPost) {
      settings = pageContext.settings
    }

    if (isBlog) {
      publisherLogo = resolve(config.siteUrl, settings?.logo || config.siteIcon)
    }

    if (isBlogPost) {
      post = pageContext.post
      postAuthor = post.authors[0]

      tags = post.tags
      primaryTag = post.tags[0]

      const shareImageUrl = post.feature_image || settings?.cover_image

      pageTitle = `${post.meta_title || post.title || title} | ${
        config.siteTitleMeta
      }`
      description = post.meta_description || post.excerpt || description

      twitterTitle = post.twitter_title ?? pageTitle
      twitterDescription = post.twitter_description ?? description
      ogTitle = post.og_title ?? pageTitle
      ogDescription = post.og_description ?? description

      if (shareImageUrl) {
        shareImage = {
          url: shareImageUrl,
          width: config.shareImageWidth,
          height: config.shareImageHeight,
        }
      }
    }

    if (isAuthorPage) {
      postAuthor = pageContext.author

      if (postAuthor.profile_image) {
        shareImage = {
          url: postAuthor.profile_image,
          width: config.shareImageWidth,
          height: config.shareImageHeight,
        }
      }
    }

    return (
      <>
        <html lang="en" />
        <title>{pageTitle}</title>
        <meta name="description" content={description} />
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        {noIndex ? <meta name="robots" content="noindex" /> : null}
        {/* OpenGraph Metadata */}
        <meta property="og:site_name" content="Goodcover Renters Insurance" />
        <meta
          property="og:type"
          content={isBlogPost ? 'article' : author ? 'profile' : 'website'}
        />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        <meta property="og:image" content={shareImage.url} />
        <meta property="og:image:width" content={shareImage.width} />
        <meta property="og:image:height" content={shareImage.height} />
        {isBlogPost ? (
          <>
            <meta
              property="article:published_time"
              content={post.published_at}
            />
            <meta property="article:modified_time" content={post.updated_at} />
            {tags.map((tag, i) => (
              <meta property="article:tag" content={tag.name} key={i} />
            ))}
            {postAuthor.facebook && (
              <meta
                property="article:author"
                content={`https://www.facebook.com/${postAuthor.facebook.replace(
                  /^\//,
                  ''
                )}/`}
              />
            )}
            <meta name="twitter:label1" content="Written by" />
            <meta name="twitter:data1" content={postAuthor.name} />
          </>
        ) : null}
        {/* Twitter Metadata */}
        <meta name="twitter:title" content={twitterTitle} />
        <meta name="twitter:description" content={twitterDescription} />
        {canonicalUrl && <meta name="twitter:url" content={canonicalUrl} />}
        <meta
          property="twitter:site"
          content={settings?.twitter || '@Goodcover'}
        />
        <meta
          property="twitter:creator"
          content={settings?.twitter || '@Goodcover'}
        />
        {primaryTag ? (
          <>
            <meta name="twitter:label2" content="Filed under" />
            <meta name="twitter:data2" content={primaryTag.name} />
          </>
        ) : null}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={shareImage.url} />
        {showLDJson ? (
          <script type="application/ld+json">{`
          {
            "@context": "https://schema.org/",
            "@type": ${
              isBlogPost
                ? '"Article"'
                : author
                  ? '"Person"'
                  : tag
                    ? '"Series"'
                    : '"WebSite"'
            },
            ${author ? `"name": ${JSON.stringify(postAuthor.name)},` : ''}
            ${
              isBlogPost
                ? `
            "datePublished": ${JSON.stringify(post.published_at)},
            "dateModified": ${JSON.stringify(post.updated_at)},
            "author": {
              "@type": "Person",
              "name": ${JSON.stringify(author?.name || postAuthor.name)}
              ${
                postAuthor.profile_image
                  ? `, "image": ${JSON.stringify(postAuthor.profile_image)}`
                  : ''
              }
              ${
                postAuthor.sameAsArray
                  ? `, "sameAs": ${JSON.stringify(postAuthor.sameAsArray)}`
                  : ''
              }
            },`
                : ''
            }
            ${
              tags?.length
                ? `"keywords": ${JSON.stringify(
                    tags.map(tag => tag.name).join(', ')
                  )},`
                : ''
            }
            ${isBlogPost ? `"headline": ${JSON.stringify(title)},` : ''}
            "url": ${JSON.stringify(canonicalUrl)},
            ${
              shareImage
                ? `"image": {
                "@type": "ImageObject",
                "url": ${JSON.stringify(shareImage.url)},
                "width": ${JSON.stringify(shareImage.width)},
                "height": ${JSON.stringify(shareImage.height)}
              },`
                : ''
            }
            ${
              isBlog && !tag
                ? `"publisher": {
                "@type": "Organization",
                "name": ${JSON.stringify(settings.title)},
                "logo": {
                  "@type": "ImageObject",
                  "url": ${JSON.stringify(publisherLogo)},
                  "width": 60,
                  "height": 60
                }
              },`
                : ''
            }
            "description": ${JSON.stringify(description)},
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": ${JSON.stringify(config.siteUrl)}
            }
          }
        `}</script>
        ) : null}
      </>
    )
  }
  return HeadComponent
}
